import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg, useMe } from "@/lib/api";
import { LoginButton } from "@/components/site/UserMenu";

export const CITY_FIELDS = [
  { k: "الاسم داخل اللعبة", ph: "مثال: Abdulaziz Abdullah" },
  { k: "العمر الحقيقي", type: "number" },
  { k: "السكور / Steam-ID", ph: "steam:110000..." },
  { k: "عدد ساعات اللعب", type: "number" },
  { k: "نبذة عن شخصيتك وخبرتك في الرول بلاي", area: true },
];

export const DEPT_FIELDS = [
  { k: "الاسم داخل اللعبة" },
  { k: "العمر الحقيقي", type: "number" },
  { k: "عدد ساعات اللعب", type: "number" },
  { k: "خبرتك السابقة في نفس القسم", area: true },
  { k: "ليش تبي تنضم للقسم؟", area: true },
  { k: "أوقات تواجدك", ph: "مثال: يومياً من 8 مساءً" },
];

export function Success({ message }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, []);
  return (
    <div ref={ref} className="glass rise rounded-3xl border-emerald-400/30 p-8 sm:p-10" data-testid="apply-success">
      <CheckCircle2 size={44} className="text-emerald-400" />
      <p className="mt-5 whitespace-pre-line text-sm leading-8 text-slate-200" data-testid="apply-success-message">{message}</p>
    </div>
  );
}

export default function ApplyForm({ type, fields, next, closed }) {
  const { data: me, isLoading } = useMe();
  const qc = useQueryClient();
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  if (done) return <Success message={done} />;
  if (closed) return <div className="glass rounded-2xl p-8 text-center" data-testid="apply-closed"><h3 className="font-disp text-xl font-bold">التقديم مقفل حالياً</h3><p className="mt-2 text-sm text-slate-400">تابع الديسكورد عشان يوصلك إعلان فتح التقديم.</p></div>;
  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-slate-900/60" />;
  if (!me) return (
    <div className="glass rounded-2xl p-8 text-center" data-testid="apply-login-required">
      <h3 className="font-disp text-xl font-bold">سجّل دخولك بالديسكورد أول</h3>
      <p className="mt-2 text-sm text-slate-400">عشان نربط التقديم بحسابك ونتواصل معك للمقابلة.</p>
      <div className="mt-6"><LoginButton next={next} testid="apply-login-btn" /></div>
    </div>
  );
  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => !String(vals[f.k] || "").trim());
    if (missing) { setErr(`عبّي خانة: ${missing.k}`); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/applications", { type, answers: vals });
      setDone(r.data.message);
      qc.invalidateQueries({ queryKey: ["myapps"] });
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="glass space-y-5 rounded-3xl p-7 sm:p-9" data-testid={`apply-form-${type}`}>
      <div className="flex items-center gap-3 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-3 text-sm">
        {me.avatar && <img src={me.avatar} alt="" className="h-8 w-8 rounded-full" />}
        <span>بتقدّم بحساب <b dir="ltr">@{me.username}</b></span>
      </div>
      {fields.map((f, i) => (
        <label key={f.k} className="block">
          <span className="mb-2 block text-sm text-slate-300">{f.k}</span>
          {f.area
            ? <textarea rows={4} className="field" placeholder={f.ph} value={vals[f.k] || ""} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} data-testid={`apply-field-${i}`} />
            : <input type={f.type || "text"} className="field" placeholder={f.ph} value={vals[f.k] || ""} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} data-testid={`apply-field-${i}`} />}
        </label>
      ))}
      {err && <p className="text-sm text-rose-400" data-testid="apply-error">{err}</p>}
      <button disabled={busy} className="btn-ice w-full" data-testid="apply-submit-btn">{busy ? "جاري الإرسال..." : "إرسال التقديم"}</button>
    </form>
  );
}
